import { inject, Injectable } from "@angular/core";
import { BiTranslateService } from "@globals/bi-translate";
import { isStringNullOrEmpty } from "@globals/helper-functions";
import { Confirmation, ConfirmationService } from "primeng/api";

@Injectable({
  providedIn: "root"
})
export class BiDialogBaseService {
  protected confirmationService = inject(ConfirmationService);
  protected translator = inject(BiTranslateService);

  /**
   * Opens dialog window with a header and dismiss button inside. This makes use of Prime Ng ConfirmDialog, which we have placed inside AppComponent's html
   * @param callback Optional callback - if not passed, dialog window will close itself.
   * @param icon Icon class name. This will display an icon next to the message
   * @param dialogKey Unique identifier for the Prime Ng ConfirmDialog being opened. Default is "app-dialog" which is set for the main dialog in AppComponent (BiDialogComponent)
   */
  public openSimpleDismissable(title: string, text?: string, icon?: string, callback?: () => void) {
    // Currently, Prime Ng has a bug causing the header to be displayed twice - in header and in message! We handle this by adding the header to the message (whether there's a message or not).
    this.confirmationService.confirm({
      closable: true,
      message: `<span class="p-dialog-title">${title}</span> ${text ? "<br/><br/>" + text : ""}`,
      accept: callback,
      acceptLabel: "OK",
      acceptVisible: true,
      rejectVisible: false,
      icon: icon
    });
  }

  /**
   * Like the openConfirm but with a red accept button and less parameters.
   * For delete-actions or something that has an important outcome
   * @param targetElement: The element that triggered the dialog opening (typically a button)
   */
  public openConfirmWarning(header: string, acceptCallback: () => void, message?: string, rejectCallback?: () => void, targetElement?: HTMLElement) {
    if (isStringNullOrEmpty(header)) throw "No header provided for dialog!";

    const confirmation: Confirmation = {
      header: header,
      message: message,
      accept: acceptCallback,
      acceptLabel: this.translator.instant("shared.Yes"),
      acceptButtonStyleClass: "p-button-danger confirmDialogOkButton", // This class confirmDialogOkButton is used by the UI tests to find the Ok button.
      reject: rejectCallback,
      rejectLabel: this.translator.instant("shared.No"),
      acceptVisible: true,
      rejectVisible: true,
      target: targetElement
    };
    +(
      // Make the "Close" button get focus by default. PrimeNg has a bug causing the "Accept" button to always get focus se we must override that behaviour
      setTimeout(() => (document.getElementsByClassName("p-dialog-header-close")[0] as HTMLButtonElement).focus(), 200)
    );
    return this.confirmationService.confirm(confirmation);
  }

  /**
   * Opens dialog window used for something to confirm. This is a dialog that we use alot of places and therefore we have this
   * helper funtion.
   * @param buttonTranslationKeys: To control the text being displayed on the buttons for yes/no. Default is keys for "Yes" and "No"
   * @param dialogKey Unique identifier for the Prime Ng ConfirmDialog being opened. Default is "app-dialog" which is set for the main dialog in AppComponent (BiDialogComponent)
   * @param targetElement: The element that triggered the dialog opening (typically a button)
   */
  public openConfirm(
    header: string,
    acceptCallback: () => void,
    rejectCallback?: () => void,
    message?: string,
    buttonTranslationKeys?: { acceptKey: string; rejectKey: string },
    targetElement?: HTMLElement,
    acceptVisible = true,
    rejectVisible = true
  ) {
    const confirmation: Confirmation = {
      header: header,
      message: message,
      accept: acceptCallback,
      closable: false, // we provide a close button through template
      acceptVisible: acceptVisible,
      acceptButtonStyleClass: "p-confirm-dialog-accept confirmDialogOkButton", // This class confirmDialogOkButton is used by the UI tests to find the Ok button.
      rejectVisible: rejectVisible,
      acceptLabel: this.translator.instant(buttonTranslationKeys?.acceptKey ?? "shared.Yes"),
      rejectLabel: this.translator.instant(buttonTranslationKeys?.rejectKey ?? "shared.No"),
      target: targetElement
    };
    if (rejectCallback) confirmation.reject = rejectCallback;

    // Make the "Close" button get focus by default. PrimeNg has a bug causing the "Accept" button to always get focus se we must override that behaviour
    setTimeout(() => (document.getElementsByClassName("p-dialog-header-close")[0] as HTMLButtonElement).focus(), 200);
    this.confirmationService.confirm(confirmation);
  }
}
